import type { CallStats } from '@/types';

/** Per-extension counts from Yeastar's call statistics, shared by Today's Stats and the Daily Activity report. */

/** One extension's line in Today's Stats. */
export interface ExtensionActivity {
  ext_num: string;
  ext_name: string;
  received: number;
  made: number;
  missed: number;
  duration: number;
}

export function statCallTotal(stat: CallStats): number {
  return (
    stat.total_call_count ||
    (stat.answered_calls || 0) + statMissed(stat)
  );
}

/** Not answered: no answer, busy, failed, or the caller hung up while it rang (abandoned). */
export function statMissed(stat: CallStats): number {
  return (
    (stat.no_answer_calls || 0) + (stat.busy_calls || 0) + (stat.failed_calls || 0) + (stat.abandoned_calls || 0)
  );
}

/** Inbound and outbound statistics per extension, as Today's Stats shows them. */
export function mergeDirectionStats(inbound: CallStats[], outbound: CallStats[]): ExtensionActivity[] {
  const map = new Map<string, ExtensionActivity>();

  const ensure = (stat: CallStats) => {
    const key = String(stat.ext_num || '');
    if (!key) return null;
    let row = map.get(key);
    if (!row) {
      row = { ext_num: key, ext_name: stat.ext_name || key, received: 0, made: 0, missed: 0, duration: 0 };
      map.set(key, row);
    } else if (stat.ext_name && row.ext_name === row.ext_num) {
      row.ext_name = stat.ext_name;
    }
    return row;
  };

  for (const stat of inbound) {
    const row = ensure(stat);
    if (!row) continue;
    row.received += statCallTotal(stat);
    // Missed is inbound no-answer/busy/failed/abandoned
    row.missed += statMissed(stat);
    row.duration += stat.total_talking_time || 0;
  }

  for (const stat of outbound) {
    const row = ensure(stat);
    if (!row) continue;
    row.made += statCallTotal(stat);
    row.duration += stat.total_talking_time || 0;
  }

  return Array.from(map.values()).sort((a, b) => a.ext_num.localeCompare(b.ext_num, undefined, { numeric: true }));
}
